
import React, { useState, useEffect } from 'react';
import { Search, Globe, Link as LinkIcon, ExternalLink, Loader2, Sparkles, TrendingUp } from 'lucide-react';
import { searchGrounding } from '../services/gemini';
import { AppLanguage } from '../types';
import { translations } from '../services/translations';

interface SearchViewProps {
  onUseCredit: () => boolean;
  isPro: boolean;
  language: AppLanguage;
  initialQuery?: string;
  onSearchPerformed?: (query: string) => void;
}

const SearchView: React.FC<SearchViewProps> = ({ onUseCredit, isPro, language, initialQuery = '', onSearchPerformed }) => {
  const t = (translations[language] as any).search || (translations['en'] as any).search;
  const [query, setQuery] = useState(initialQuery);
  const [result, setResult] = useState<{ text: string; sources: any[] } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
      handleSearch(initialQuery);
    }
  }, [initialQuery]);

  const handleSearch = async (overrideQuery?: string) => {
    const searchQuery = overrideQuery || query.trim();
    if (!searchQuery || isLoading) return;

    if (!onUseCredit()) return;

    setIsLoading(true);
    setResult(null);
    try {
      const data = await searchGrounding(searchQuery, isPro);
      setResult(data);
      if (onSearchPerformed) onSearchPerformed(searchQuery);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSearchContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('###')) {
        return (
          <h3 key={i} className="text-base font-bold text-slate-900 dark:text-white mt-8 mb-4 flex items-center gap-2">
            <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
            {line.replace(/###/g, '').trim().replace(/\*\*/g, '')}
          </h3>
        );
      }

      if (line.startsWith('* ')) {
        const bulletText = line.replace('* ', '');
        const segments = bulletText.split('**').filter(s => s.length > 0);
        
        return (
          <div key={i} className="mb-3 pl-1 flex gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 flex-shrink-0"></div>
            <p className="text-slate-700 dark:text-gray-300 leading-relaxed text-sm">
              {segments.map((seg, idx) => {
                const isBold = bulletText.includes(`**${seg}**`);
                return (
                  <span key={idx} className={isBold ? 'text-slate-900 dark:text-white font-bold' : ''}>
                    {seg}
                  </span>
                );
              })}
            </p>
          </div>
        );
      }

      if (line.trim().length > 0) {
        return <p key={i} className="text-slate-600 dark:text-gray-400 leading-relaxed mb-4 text-[13px]">{line}</p>;
      }
      return null;
    });
  };

  // Helper to pick icon for suggestions
  const getIcon = (idx: number) => {
    if (idx === 0 || idx === 3) return <TrendingUp size={14} />;
    if (idx === 1) return <Globe size={14} />;
    return <Sparkles size={14} />;
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 h-full flex flex-col transition-colors duration-300">
      <div className="mb-10 text-center animate-in fade-in slide-in-from-top-4 duration-700">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">{t.title}</h1>
        <p className="text-slate-400 dark:text-gray-500 font-medium text-sm">{t.sub}</p>
      </div>

      <div className="relative mb-10 group">
        <div className="relative flex items-center">
          <div className="absolute left-5 transition-colors duration-300">
            {isLoading ? (
              <Loader2 size={20} className="text-blue-600 dark:text-blue-500 animate-spin" />
            ) : (
              <Search size={20} className="text-slate-300 dark:text-gray-500 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-500" />
            )}
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={t.placeholder}
            className="w-full bg-slate-50 dark:bg-[#111] border border-slate-200 dark:border-white/5 text-slate-900 dark:text-white rounded-[1.5rem] py-4 pl-14 pr-32 focus:outline-none focus:border-blue-600/30 dark:focus:border-blue-500/30 transition-all shadow-sm dark:shadow-xl text-base font-medium placeholder:text-slate-300 dark:placeholder:text-gray-700"
          />
          <button
            onClick={() => handleSearch()}
            disabled={!query.trim() || isLoading}
            className={`absolute right-2 px-6 py-2 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg active:scale-95 ${
              !query.trim() || isLoading
              ? 'bg-slate-200 dark:bg-white/5 text-slate-400 dark:text-white/20 cursor-not-allowed opacity-80'
              : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/10'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={16} />
                <span>{t.searching}</span>
              </>
            ) : (
              <span>{t.button}</span>
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pb-12">
        {!result && !isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {t.suggestions.map((s: any, i: number) => (
              <button
                key={s.text}
                onClick={() => { setQuery(s.text); handleSearch(s.text); }}
                className="bg-white dark:bg-[#111] border border-slate-200 dark:border-white/5 p-5 rounded-2xl text-left text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-[#161616] transition-all flex items-center justify-between group shadow-sm dark:shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="text-blue-600/50 dark:text-blue-500/50 group-hover:text-blue-600 dark:group-hover:text-blue-500">
                    {getIcon(i)}
                  </div>
                  <span className="text-sm font-medium">{s.text}</span>
                </div>
                <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="flex flex-col items-center justify-center h-48 space-y-4">
            <Globe size={32} className="text-blue-600 dark:text-blue-500 animate-spin-slow" />
            <p className="text-slate-400 dark:text-gray-500 font-bold tracking-widest text-[10px] uppercase">{t.accessing}</p>
          </div>
        )}

        {result && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-6 duration-700">
            <div className="bg-white dark:bg-[#0c0c0c] border border-slate-200 dark:border-white/5 rounded-[2rem] p-8 md:p-10 shadow-sm dark:shadow-2xl">
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-100 dark:border-white/5">
                <Globe className="text-blue-600 dark:text-blue-500" size={20} />
                <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">{t.analysis}</h3>
              </div>
              <div className="search-result-content">
                {renderSearchContent(result.text)}
              </div>
            </div>

            {result.sources.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-[10px] font-bold text-slate-400 dark:text-gray-700 tracking-wider flex items-center gap-2 px-1 uppercase">
                  <LinkIcon size={12} /> {t.sources}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {result.sources.map((source, i) => (
                    <a
                      key={i}
                      href={source.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white dark:bg-[#111] border border-slate-200 dark:border-white/5 p-4 rounded-xl hover:bg-slate-50 dark:hover:bg-[#161616] transition-all flex items-start gap-3 group shadow-sm"
                    >
                      <div className="bg-slate-100 dark:bg-[#1a1a1a] w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-all">
                        <Globe size={14} className="text-slate-400 dark:text-gray-600 group-hover:text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-slate-900 dark:text-white font-bold text-xs truncate mb-0.5">{source.title}</p>
                        <p className="text-[10px] text-slate-400 dark:text-gray-600 truncate">{new URL(source.uri).hostname}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchView;
